import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Stack,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  getHrUserRoles,
  createHrUserRole,
  updateHrUserRole,
  deleteHrUserRole,
} from "../../api/api";

const ROLES = [
  { label: "HR Admin", value: "hr" },
  { label: "Super Admin", value: "super_admin" },
];

export default function HrUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("hr");
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editRole, setEditRole] = useState("hr");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showMessage = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await getHrUserRoles();
      setRows(data);
    } catch (e) {
      showMessage(e.message || "Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAdd = async () => {
    if (!email.trim()) {
      return showMessage("Please enter employee email", "error");
    }

    try {
      setSaving(true);
      const result = await createHrUserRole({ email, role });
      showMessage(result.message);
      setEmail("");
      setRole("hr");
      loadData();
    } catch (e) {
      showMessage(e.message || "Unable to assign role", "error");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setSelected(row);
    setEditRole(row.role);
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      const result = await updateHrUserRole(selected.user_id, {
        email: selected.email,
        role: editRole,
      });
      showMessage(result.message);
      setEditOpen(false);
      loadData();
    } catch (e) {
      showMessage(e.message || "Unable to update role", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove ${row.name}?`)) return;

    try {
      const result = await deleteHrUserRole(row.id);
      showMessage(result.message);
      loadData();
    } catch (e) {
      showMessage(e.message || "Unable to delete", "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          HR User Management
        </Typography>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Assign Role
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: "flex-start" }}>
              <TextField
                label="Employee Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                sx={{ flexGrow: 1, minWidth: 280 }}
              />
              <TextField
                select
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                size="small"
                sx={{ width: 180 }}
              >
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={saving}
                sx={{ whiteSpace: "nowrap" }}
              >
                {saving ? "Saving..." : "Assign"}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {/* ================================================= */}
      {/* TABLE */}
      {/* ================================================= */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, background: "#f5f7fa", whiteSpace: "nowrap" } }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2">{row.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                    <Typography variant="body2">
                        {ROLES.find((r) => r.value === row.role)?.label || row.role}
                    </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit role">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEdit(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ================================================= */}
      {/* EDIT DIALOG */}
      {/* ================================================= */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Update Role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email"
              value={selected?.email || ""}
              disabled
              size="small"
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={editRole}
              onChange={(e) => setEditRole(e.target.value)}
              size="small"
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={saving}>
            {saving ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================= */}
      {/* SNACKBAR */}
      {/* ================================================= */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack({ ...snack, open: false })}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}